import dataModel from '../../models/DataModel';
import { RecordStates } from'../../constants/ReduxConstants';

const hierarchalItemTypes = new Set(['build']);
const normalizeHooks = new Map();

function getAction(key, item, actionType) {
  let actionRecord = { type: actionType };
  if (actionType.includes('_UNSET')) {
    actionRecord.id = item.id;
  }
  else if (actionType.includes('_CLEAR')) {
    return actionRecord;
  }
  else {
    actionRecord[mapToStoreName(key)] = item;
  }
  return actionRecord;
}

function mapToPlural(item){
  switch (item) {
  case 'completioncriterion':
    return 'completionCriteria';
  case 'completion_criterion':
    return 'completion_criteria';
  case 'analysis_completion_criterion':
    return 'analysis_completion_criteria';
  case 'vendor_company':
    return 'vendor_companies';
  case 'company':
    return 'companies';
  case 'directory':
    return 'directories';
  case 'eventhistory':
    return 'eventHistories';
  case 'event_history':
    return 'event_histories';
  case 'projecttemplate':
    return 'projectTemplates';
  case 'projecttype':
    return 'projectTypes';
  case 'inventory':
    return 'inventories';
  case 'activity':
    return 'activities';
  case 'task_dependency':
    return 'task_dependencies';
  default:
    return item + 's';
  }  
}

function mapToSingular(item) {
  switch (item) {
  case 'companies':
    return 'company';
  case 'directories':
    return 'directory';
  case 'vendor_companies':
    return 'vendor_company';
  case 'completion_criteria':
    return 'completion_criterion';
  case 'analysis_completion_criteria':
    return 'analysis_completion_criterion';
  case 'event_histories':
    return 'event_history';
  case 'activities':
    return 'activity';
  case 'task_dependencies':
    return 'task_dependency';
  default:
    if (item.toLowerCase().endsWith('s')) {
      return item.substr(0, item.length - 1);
    }
    else {
      return item;
    }
  }
}

function mapToStoreName(itemType) {
  switch (itemType.toLowerCase()) {
  case 'primary_company':
    return 'company';
  case 'all_closeout':
    return 'closeout';
  case 'all_location':
    return 'location';
  case 'document':
  case 'site_photo':
    return 'attachment';
  case 'project_manager':
  case 'vendor_user':
  case 'user_who_performed_action':
  case 'build_owner':
  case 'lead_user':
    return 'user';
  case 'admin':
  case 'contact':
    return 'user';
  case 'task_dependent_on':
    return 'task';
  case 'task_counts':
      // leave these alone...
    return itemType.toLowerCase();
  default:
    return mapToSingular(itemType.toLowerCase());
  }
}

function mapToRecord(itemType) {
  switch (itemType.toLowerCase()) {
  case 'primary_company':
    return 'company';
  case 'all_closeouts':
    return 'closeout';
  case 'all_locations':
    return 'location';
  case 'document':
  case 'site_photo':
    return 'attachment';
  case 'project_manager':
  case 'build_owner':
  case 'vendor_user':
  case 'user_who_performed_action':
  case 'lead_user':
    return 'user';
  case 'admin':
  case 'contact':
    return 'user';
  case 'task_dependent_on':
    return 'task';
  case 'task_counts':
      // leave these alone
    return itemType.toLowerCase();
  default:
    return mapToSingular(itemType.toLowerCase());
  }
}

function mapToReducer(itemType) {
  const reducer = 'new_' + mapToPlural(mapToRecord(itemType));
  return reducer;
}

function dispatchGenericAction(actionName, itemName, item) {
  dataModel.store.dispatch({ type: actionName, [itemName]: item });
}

function setItemInStore(itemType, item) {
  item._cached_at = new Date();

  if (itemType.toLowerCase() == 'attachment') {
    if (item.upload_thumb) {
      item._thumbnailForRetention = new Image();
      item._thumbnailForRetention.src = item.upload_thumb;
    }
  }
  const existingItem = findItem(itemType, item.id);
  if (existingItem) {
    for (let key in existingItem) {
      if (item[key] === undefined) {
        item[key] = existingItem[key];
      }
    }
    if (existingItem.is_favorite && item.is_favorite != false) {
      item.is_favorite = true;
    }
    if (existingItem.is_offline_project && item.is_offline_project != false) {
      item.is_offline_project = true;
    }
  }
  dataModel.store.dispatch(getAction(itemType.toLowerCase(), item, mapToRecord(itemType).toUpperCase() + '_SET'));
}

function setItemInStoreWithHooks(itemType, item) {
  itemType = itemType.toLowerCase();
  if (normalizeHooks.has(itemType)) {
    for (const normalizeHook of normalizeHooks.get(itemType)) {
      item = normalizeHook(itemType, item);
    }
  }
  setItemInStore(itemType, item);
}

function deleteItemInStore(itemType, itemId) {
  const itemToBeDeleted = findItem(itemType, itemId);
  dataModel.store.dispatch(getAction(itemType.toLowerCase(), { id: itemId }, mapToRecord(itemType).toUpperCase() + '_UNSET'));
  return itemToBeDeleted;
}

function clearItemsInStore(itemType) {
  dataModel.store.dispatch(getAction(itemType.toLowerCase(), null, mapToRecord(itemType).toUpperCase() + '_CLEAR'));
}

function normalizeAndSyncCollectionToStore(rootItemType, apiResponse) {
  rootItemType = rootItemType.toLowerCase();
  let ids = [];
  const recordList = apiResponse.data;
  for (let rootRecord of recordList) {
    ids.push(rootRecord.attributes.id);
    const normalizedRootRecordRelationships = _normalizeRecordRelationships(rootRecord.attributes.id, { ...rootRecord.relationships });
    setItemInStoreWithHooks(rootItemType, { _recordState: RecordStates.SYNCED, ...rootRecord.attributes, ...normalizedRootRecordRelationships });
  }
  _syncIncludesToStore(apiResponse);
  return ids;
}

function normalizeAndSyncToStore(rootItemType, apiResponse, options) { 
  rootItemType = rootItemType.toLowerCase();
  if (apiResponse) {
    if (apiResponse.data && Array.isArray(apiResponse.data)) {
      let normalizedArray = [];
      for (let dataItem of apiResponse.data) {
        let rootRecord = Object.assign({}, dataItem);

        const normalizedRootRecordRelationships = _normalizeRecordRelationships(rootRecord.attributes.id, { ...rootRecord.relationships });
        const normalizedRootRecord = { ...rootRecord.attributes, ...normalizedRootRecordRelationships };
        if (!options || options['store'] == undefined || options.store) {
          normalizedRootRecord._recordState = RecordStates.SYNCED;
          setItemInStoreWithHooks(rootItemType, normalizedRootRecord);
          normalizedArray.push(normalizedRootRecord);
        }
      }
      _syncIncludesToStore(apiResponse);
      return normalizedArray;
    }
    if (apiResponse.data || !Array.isArray(apiResponse)) {
      let rootRecord = Object.assign({}, apiResponse.data ? apiResponse.data : apiResponse);

      const normalizedRootRecordRelationships = _normalizeRecordRelationships(rootRecord.attributes.id, { ...rootRecord.relationships });
      const normalizedRootRecord = { ...rootRecord.attributes, ...normalizedRootRecordRelationships };
      if (!options || options['store'] == undefined || options.store) {
        normalizedRootRecord._recordState = RecordStates.SYNCED;
        setItemInStoreWithHooks(rootItemType, normalizedRootRecord);
        _syncIncludesToStore(apiResponse);
      }
      return normalizedRootRecord;
    }
    else {
      if (Array.isArray(apiResponse)) {
        let normalizedArray = [];
        for (let item of apiResponse) {
          item._recordState = RecordStates.SYNCED;
          let normalizedItem = normalizeItem(rootItemType, item);
          setItemInStoreWithHooks(rootItemType, normalizedItem);
          normalizedArray.push(normalizedItem);
        }
        return normalizedArray;
      }
      else {
        return null;
      }
    }
  }
}

function _normalizeRecordRelationships(recordId, relationshipsObject) {
  for (let relationshipKey in relationshipsObject) {
    if (relationshipsObject[relationshipKey]) {
      const relationshipsItem = relationshipsObject[relationshipKey];
      if (!relationshipsItem.data) {
        // Relationship was explicitly cleared by the server (e.g. lead_user
        // removed). Set the id to null so setItemInStore overwrites the stale
        // value instead of treating an undefined id as "no info" and keeping
        // the old one. Mirrors _syncIncludesToStore's handling of null data.
        relationshipsObject[relationshipKey + '_id'] = null;
        delete relationshipsObject[relationshipKey];
      }
      else if (Array.isArray(relationshipsItem.data)) {
        relationshipsObject[relationshipKey] = relationshipsItem.data
          .filter(d => d != null)
          .map(d => parseInt(d.id, 10));
      }
      else if (relationshipsItem.data && relationshipsItem.data.id != null) {
        relationshipsObject[relationshipKey + '_id'] = parseInt(relationshipsItem.data.id, 10);
        delete relationshipsObject[relationshipKey];
        //we need to delete the old property
      }
      else {
        // data present but no id (e.g. { data: {} }) — treat as cleared too.
        relationshipsObject[relationshipKey + '_id'] = null;
        delete relationshipsObject[relationshipKey];
      }
    }
  }
  return relationshipsObject;
}

function _syncIncludesToStore(data) {
  if (data.included) {
    for (let included of data.included) {
      let itemType = included.type;
      let record = Object.assign({}, included.attributes);
      if (record.id == null && included.id != null) record.id = parseInt(included.id, 10);
      for (let relationshipKey in included.relationships) {
        if (relationshipKey) {
          if (Array.isArray(included.relationships[relationshipKey].data)) {
            record[relationshipKey] = included.relationships[relationshipKey].data.map(d => {return parseInt(d.id, 10);});
          }
          else {
            if (included.relationships[relationshipKey].data) {
              record[relationshipKey + '_id'] = parseInt(included.relationships[relationshipKey].data.id, 10);
            } else {
              record[relationshipKey + '_id'] = null;
            }
          }
        }
      }
      record._recordState = RecordStates.SYNCED;
      setItemInStoreWithHooks(itemType, record);
    }
  }
}

function normalizeItem(itemType, item, options) {
  let newItem = Object.assign({}, item);
  for (let key in newItem) {
    if (key && !ignoreItemKey(key, options)) {
      let element = newItem[key];
      if (Array.isArray(element)) {
        newItem[key] = normalizeCollection(key, element, options);
      }
      else if (key == 'parent') {
        delete newItem[key];
      }
      else if (element != null && typeof element == 'object' && key != '_recordState' && key != '_cached_at' && key != '_thumbnailForRetention') {
        if (element.id){
          let normalizedItem = normalizeItem(key, element, options);
          if (normalizedItem) {
            newItem[key + '_id'] = normalizedItem.id;
            delete newItem[key];
          }
        }
      }
    }
  }
  if (options && options.store) {
    setItemInStoreWithHooks(itemType, newItem);
  }
  return newItem;
}

//TODO: check logic
function normalizeCollection(itemType, items, options) {
  return items.map(item => {
    if (typeof item != 'number' && typeof item != 'string') {
      const normalizedItem = normalizeItem(mapToSingular(itemType), item, options);
      return normalizedItem.id;
    }
    else {
      return item;
    }
  });
}

function dontDenormalizeField(key) {
  switch(key){
  case 'buildable_id':
  case 'attachable_id':
  case 'closeoutable_id':
  case 'commentable_id':
  case 'eventable_id':
  case 'notifiable_id':
  case 'convertable_id':
  case 'root_build_id':
  case 'build_id':
    return true;
  default:
    return false;
  }
} 

function denormalizeCollection(collectionType, ids, recordDescriptor = null) {
  collectionType = collectionType.toLowerCase();
  const recordDescriptorForItem = recordDescriptor && recordDescriptor[mapToSingular(collectionType)] ? recordDescriptor[mapToSingular(collectionType)] : recordDescriptor;
  const filteredIds = !ids ? [] : ids.filter(id => {
    return itemExistsInStore(collectionType, id);
  });
  const denormalizedCollection = filteredIds.map(id => {
    return denormalizeItem(collectionType, id, recordDescriptorForItem);
  });
  return denormalizedCollection;
}

function denormalizeItem(itemType, item, recordDescriptor) {
  if (recordDescriptor && recordDescriptor[itemType]) {
    recordDescriptor = recordDescriptor[itemType];
  }
  if (typeof item == 'number'){
    let foundItem = findItem(itemType, item);
    if (!foundItem) {
      return { id: item };
    }
    else {
      item = foundItem;
    }
  }
  if (!item){
    return;
  }
  let denormalizedItem = {};

  for (let key in item) {
    if (key == '_thumbnailForRetention') {
      denormalizedItem[key] = item[key];
    }
    else if (key) {
      if (key.endsWith('_id') && typeof item[key] == 'number') {
        denormalizedItem[key] = item[key];
        let alteredKey = key.substr(0, key.length - 3);
        if (recordDescriptor && recordDescriptor[alteredKey]) {
          if (dataModel.stateTree[mapToReducer(alteredKey)]) {
            let element = Object.assign({}, dataModel.stateTree[mapToReducer(alteredKey)][item[key]]);
            denormalizedItem[alteredKey] = denormalizeItem(alteredKey, element, recordDescriptor[alteredKey]);
          }
        }
      }
      else if (Array.isArray(item[key])) {
        if (recordDescriptor && (key in recordDescriptor)) {
          if (item[key].length == 0) {
            denormalizedItem[key] = [];
          }
          else {
            let collectionRecordDescriptor = Object.assign({}, recordDescriptor[key]);
            if (hierarchalItemTypes.has(mapToSingular(key))) {
              // handle situations like builds.builds.builds... 
              collectionRecordDescriptor[key] = recordDescriptor[key];
            }
            denormalizedItem[key] = denormalizeCollection(mapToRecord(key), item[key], collectionRecordDescriptor);
          }
        }
        else {
          denormalizedItem[key] = item[key];
        }
      }
      else {
        let field = item[key];
        if (field) {
          if (key == 'zip') {
            field = field.toString();
            while (field.length < 5) {
              field = '0' + field;
            }
          }
        }
        if (!denormalizedItem[key]) {
          denormalizedItem[key] = field;
        }
      }
    }
  }
  return denormalizedItem;
}

function isComplete(itemType, item_id, recordDescriptor, checkArrays) {
  const skippedKeys = ['_recordState', '_cached_at', 'fields', 'excludeFields', '_thumbnailForRetention'];
  let item = findItem(itemType, item_id);
  if (!item) {
    return false;
  }
  if (item._recordState == RecordStates.NEEDS_ATTRIBUTE_SYNC) {
    return false;
  }
  else if (item._recordState == RecordStates.NEEDS_SYNC && !recordDescriptor) {
    return false;
  }
  else if (recordDescriptor) {
    for (let key in recordDescriptor) {
      if (!(skippedKeys.includes(key))) {
        if (key == 'additionalFields') {
          const additionalFields = recordDescriptor[key];
          // Strict: a field the API serialized as null IS loaded, it is just empty. With loose
          // equality null matched undefined, so any record with a nullable additionalField was
          // permanently "incomplete" and every read re-fetched it. That is what made project rows
          // whose project_manager has no profile_image issue a GET /projects/:id each. A key that is
          // genuinely absent (never requested) is still undefined here, so it still triggers a fetch.
          const missingFields = additionalFields.filter(field => {
            return item[field] === undefined;
          });
          if (missingFields.length > 0) {
            return false;
          }
        }
        else if (key.endsWith('_id')) {
          if (!dontDenormalizeField(key)) {
            if (key in item) {
              // maybe null
              if (item[key]) {
                const id = parseInt(item[key], 10);
                const subItemType= key.substr(0, key.length - 3);
                if (!itemExistsInStore(subItemType, id)){
                  return false;
                }
                let complete = isComplete(subItemType, item[key], recordDescriptor[key], checkArrays);
                if (!complete) {
                  return false;
                }
              }
            } else {
              return false;
            }
          }
        }
        else {
          // NOTE: the above will handle record descriptors asking for
          // "has one" or "belongs to" relationships using, for example,
          // "user_task_assignment.user_id" while below will handle
          // the same type of associations if the descriptor is, for example,
          // "user_task_assignment.user"
          const idKey = key + '_id';
          if (idKey in item) {
            // maybe null...
            if (item[idKey]) {
              const id = parseInt(item[idKey], 10);
              if (!itemExistsInStore(key, id)) {
                return false;
              }
              let complete = isComplete(key, id, recordDescriptor[key], checkArrays);
              if (!complete) {
                return false;
              }
            }
          }
          else {
            let subItemKey = key;
            if (item[subItemKey] == undefined){
              return false;
            }
            else if (Array.isArray(item[subItemKey])) {
              if (checkArrays && item[subItemKey].length == 0) {
                return false;
              }
              for (let id of item[subItemKey]) {
                if (id && typeof id == 'number'){
                  if (!itemExistsInStore(subItemKey, id)){
                    return false;
                  }
                  else{
                    let complete = isComplete(key, id, recordDescriptor[key], checkArrays);
                    if (!complete) {
                      return false;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return item._recordState == RecordStates.NEEDS_SYNC ? false : true;
  }
  return true;
}

function findItem(itemType, item_id) {
  const storeItemType = mapToStoreName(itemType);
  let stateTree = dataModel.stateTree;
  const storeForItemType = stateTree[mapToReducer(storeItemType)];
  if (storeForItemType && storeForItemType[item_id]) {
    let clonedItem = Object.assign({}, storeForItemType[item_id]);
    for (let key in clonedItem){
      if (key){
        if (Array.isArray(clonedItem[key])){
          clonedItem[key] = clonedItem[key].map(id => {return id;});
        }
      }
    }
    return clonedItem;
  }
  else{
    return null;
  }
}

function itemExistsInStore(itemType, item_id) {
  const storeItemType = mapToStoreName(itemType);
  let item = null;
  if (dataModel.stateTree[mapToReducer(storeItemType)] && dataModel.stateTree[mapToReducer(storeItemType)][item_id]) {
    item = dataModel.stateTree[mapToReducer(storeItemType)][item_id];
    if (item._recordState && item._recordState == RecordStates.DELETED) {
      delete dataModel.stateTree[mapToReducer(storeItemType)][item_id];
      item = null;
    }
  }
  if (item) {
    return true;
  }
  else{
    return false;
  }
}

function findItemByCreationId(itemType, creation_id) {
  if (dataModel.stateTree[mapToReducer(itemType)]) {
    for (let key in dataModel.stateTree[mapToReducer(itemType)]) {
      if (key) {
        const item = dataModel.stateTree[mapToReducer(itemType)][key];
        if (item.creation_id == creation_id) {
          return Object.assign({}, item);
        }
      }
    }
  }
  return null;
}

function ignoreItemKey(key, options) {
  if (options && options.ignoreOnNormalize) {
    return options.ignoreOnNormalize.indexOf(key) >= 0;
  }
  return false;
}

function addNormalizeHook(key, hook) {
  if (!normalizeHooks.has(key)) {
    normalizeHooks.set(key, []);
  }
  normalizeHooks.get(key).push(hook);
}

export default {
  mapToSingular,
  mapToPlural,
  mapToStoreName,
  mapToReducer,
  normalizeCollection,
  normalizeItem,
  normalizeAndSyncToStore,
  normalizeAndSyncCollectionToStore,
  syncIncludesToStore: _syncIncludesToStore,
  denormalizeCollection,
  denormalizeItem,
  itemExistsInStore,
  findItem,
  findItemByCreationId,
  isComplete,
  setItemInStore,
  deleteItemInStore,
  clearItemsInStore,
  dispatchGenericAction,
  addNormalizeHook,
};



// WEBPACK FOOTER //
// ./src/helpers/StoreFunctions/StoreItemProcessing.js